import type {
  TrackingConsent,
  TrackingConsentDetails,
  TranscendAPI,
  XDIAPI,
  XDIChannel,
} from '@main/ag-types';
import { view as globalThis } from '@main/utils';

const view = globalThis as any;
let xdiAPI;
/**
 * Load XDI asynchronously and cache API
 *
 * @returns XDI API
 */
// eslint-disable-next-line no-return-assign
export const getXDI = (): Promise<XDIAPI> =>
  (xdiAPI ??= new Promise((resolve, reject) => {
    // Stub Transcend XDI ready queue
    if (!view?.transcend?.xdi?.ready) {
      view.transcend = {
        ...view?.transcend,
        xdi: {
          readyQueue: [],
          /**
           * Transcend XDI ready queue registrar
           *
           * @param callback - Function to be called when XDI is ready
           */
          ready(callback: (api: TranscendAPI) => void) {
            this.readyQueue.push(callback);
          },
          ...view?.transcend?.xdi,
        },
      };
    }

    // Wait for Transcend XDI API to be ready
    view?.transcend?.xdi?.ready?.((xdi: XDIAPI) => {
      resolve(xdi);
    });
    const script = document.createElementNS(
      'http://www.w3.org/1999/xhtml',
      'script',
    ) as HTMLScriptElement;
    script.addEventListener('error', (evt) => {
      reject(evt);
    });

    // Load Transcend XDI script asynchronously
    // eslint-disable-next-line no-multi-assign
    script.async = script.defer = true;
    script.src = 'https://cdn.transcend.io/xdi.js';
    document.documentElement.append(script);
  }));

const connections = new Map<string, XDIChannel>();
/**
 * Connect to organization XDI endpoint & cache XDI connection channel
 *
 * @param bundleID - Consent manager bundle ID
 * @param syncUrl - URL used for syncing consent
 * @returns XDI connection channel
 */
export const connect = async (
  bundleID: string,
  syncUrl: string,
): Promise<XDIChannel> => {
  if (connections.has(bundleID)) {
    return connections.get(bundleID)!;
  }
  // eslint-disable-next-line no-async-promise-executor
  const channel = await new Promise<XDIChannel>(async (resolve) => {
    const xdi = await getXDI();
    resolve(await xdi.connect!(syncUrl));
  });
  connections.set(bundleID, channel);
  return channel;
};

/**
 * Get user tracking consent
 *
 * @param bundleID - Consent manager bundle ID
 * @param syncURL - The URL to use for syncing consent
 * @returns TrackingConsentDetails
 */
export const getConsent = async (
  bundleID: string,
  syncURL: string,
): Promise<TrackingConsentDetails | undefined> => {
  const channel = await connect(bundleID, syncURL);
  const { consent } = await channel.run('ConsentManager:Sync', {
    sync: ['consent'],
  });
  return consent;
};

/**
 * Sync user tracking consent
 *
 * @param syncURL - The URL to use for syncing consent
 * @param bundleID - Consent manager bundle ID
 * @param purposes - TrackingConsent
 * @param timestamp - Time this consent was originally set (ISO 8601 format)
 */
export const syncConsent = async (
  syncURL: string,
  bundleID: string,
  purposes: TrackingConsent,
  timestamp = new Date().toISOString(),
): Promise<void> => {
  const channel = await connect(bundleID, syncURL);
  await channel.run('ConsentManager:Sync', {
    sync: ['consent'],
    consent: { timestamp, purposes },
  });
};

/**
 * Sync SaleOfInfo tracking purpose consent
 *
 * @param syncURL - The URL to use for syncing consent
 * @param bundleID - Consent manager bundle ID
 * @param consent - Consent value to set
 * @param timestamp - Time this consent was originally set (ISO 8601 format)
 */
export const syncSaleOfInfoConsent = async (
  syncURL: string,
  bundleID: string,
  consent: boolean,
  timestamp = new Date().toISOString(),
): Promise<void> => {
  const purposes = { SaleOfInfo: consent };
  await syncConsent(syncURL, bundleID, purposes, timestamp);
};

const defaultTrackingPurposes = [
  'Analytics',
  'Advertising',
  'Functional',
  'SaleOfInfo',
];
/**
 * Sync all configurable tracking purpose consent
 *
 * @param syncURL - The URL used to sync consent
 * @param bundleID - Consent manager bundle ID
 * @param consent - Consent value to set
 * @param timestamp - Time this consent was originally set (ISO 8601 format)
 */
export const syncAllConsent = async (
  syncURL: string,
  bundleID: string,
  consent: boolean,
  timestamp = new Date().toISOString(),
): Promise<void> => {
  const consentSyncData = await getConsent(bundleID, syncURL);
  if (consentSyncData) {
    const { purposes } = consentSyncData;
    [
      ...new Set([...defaultTrackingPurposes, ...Object.keys(purposes ?? {})]),
    ].forEach((purpose) => {
      if (purposes) {
        purposes[purpose] = consent;
      }
    });
    await syncConsent(syncURL, bundleID, purposes ?? {}, timestamp);
  }
};
